import type { ChecklistItemPublic, LifecycleChecklistPublic } from '@accounts/shared';

import type { ItEmailSummary } from './ItEmailDialog';
import type { ItStanding } from './picker';

/** A live, open checklist item about an outside system: the work the IT email names. */
function isOpenForIt(item: ChecklistItemPublic): boolean {
  return item.status === 'open' && item.retiredAt === null && item.record !== null;
}

/**
 * Whether Draft IT email has anything to draft. The server answers 409 when no live item
 * about an outside system is still open, so the checklist card offers the button only then.
 */
export function hasItWork(checklist: LifecycleChecklistPublic): boolean {
  return checklist.items.some(isOpenForIt);
}

/**
 * THE RECORDS A CHECKLIST'S EMAIL MOVES TO WAITING ON IT (FINAL demo: the checklist's Draft
 * IT email uses the same email, with Mark as sent). Each open item's record, while it is
 * still IT work (Needs access / Remove access) and in no live IT email: the server refuses
 * a record twice, so one already drafted or sent from the Queue is left out.
 */
export function checklistItRecords(
  checklist: LifecycleChecklistPublic,
  standing: ItStanding,
): ChecklistItemPublic[] {
  const seen = new Set<string>();
  return checklist.items.filter((item) => {
    const record = item.record;
    if (!isOpenForIt(item) || record === null) return false;
    if (record.status !== 'needs_access' && record.status !== 'remove_access') return false;
    if (standing.sent.has(record.id) || standing.draft.has(record.id) || seen.has(record.id)) {
      return false;
    }
    seen.add(record.id);
    return true;
  });
}

/**
 * WHICH TEXT THE CHECKLIST'S EMAIL CARRIES. The lifecycle text names every open item about
 * an outside system; the email Mark as sent makes tracks only `checklistItRecords`. The text
 * is used only where the two sets are the same (or nothing can move, so only Copy remains).
 * Where only some can move (others already with IT, or no longer IT work), the same
 * lifecycle text is rendered over exactly those (`renderChecklistItEmail`), so IT is never
 * asked twice, each line keeps its lifecycle action, and every line it gets is one Waiting
 * on IT can account for.
 */
export type ChecklistItPlan =
  | { kind: 'checklist'; items: ChecklistItemPublic[] }
  | { kind: 'records'; items: ChecklistItemPublic[] };

export function checklistItPlan(
  checklist: LifecycleChecklistPublic,
  standing: ItStanding,
): ChecklistItPlan {
  const items = checklistItRecords(checklist, standing);
  const named = new Set(
    checklist.items.flatMap((item) =>
      isOpenForIt(item) && item.record !== null ? [item.record.id] : [],
    ),
  );
  return items.length === 0 || items.length === named.size
    ? { kind: 'checklist', items }
    : { kind: 'records', items };
}

/** The record ids of checklist items (each item here carries one). */
export function recordIdsOf(items: readonly ChecklistItemPublic[]): string[] {
  return items.flatMap((item) => (item.record === null ? [] : [item.record.id]));
}

/** The dialog's summary line over the items Mark as sent would move. */
export function checklistSummary(items: readonly ChecklistItemPublic[]): ItEmailSummary {
  const setUp = items.filter((item) => item.action === 'set_up').length;
  return {
    items: items.length,
    systems: new Set(items.map((item) => item.record?.catalogItem.id)).size,
    setUp,
    remove: items.length - setUp,
    shared: items.filter((item) => (item.record?.sharedWith.length ?? 0) > 0).length,
  };
}
